<template>
  <b-card v-if="company">
    <confirm-delete-dialog :company="company" />

    <b-container class="mb-3">
      <b-row class="mt-3">
        <b-col class="d-flex align-items-center">
          <h3 class="mb-0 form-title">Företagsinformation / Company card</h3>
        </b-col>

        <b-col class="d-flex flex-row justify-content-end align-items-center">
          <b-button
            v-if="company._id && hasSimplePermissions('deleteCompany') && !company.archived"
            pill
            variant="outline"
            class="delete-button"
            @click="$modal.show('confirmDeleteDialog', { title: 'Delete Company?' })"
          >
            <b-icon icon="trash" />
          </b-button>

          <router-link :to="{ name: 'OrganisationOverview' }">
            <b-button pill class="close-button" variant="primary"> {{ $t('Save') }} &amp; {{ $t('Close') }} </b-button>
          </router-link>
        </b-col>
      </b-row>

      <b-row class="form-container">
        <b-col cols="8">
          <b-row>
            <b-col cols="6">
              <b-custom-input
                id="company-name"
                required
                :label="$t('Name')"
                :value="company.companyName"
                class="shadow rounded"
                @blur="updateField('companyName', $event)"
              />

              <b-custom-input
                id="street"
                required
                :label="$t('Street Address')"
                :value="company.address && company.address.street"
                class="shadow rounded"
                @blur="updateAddressProperty('street', $event)"
              />

              <b-custom-input
                id="city"
                required
                :label="$t('City')"
                :value="company.address && company.address.city"
                class="shadow rounded"
                @blur="updateAddressProperty('city', $event)"
              />

              <b-custom-input
                id="email"
                required
                :label="$t('Email')"
                :value="company.email"
                class="shadow rounded"
                @blur="updateField('email', $event)"
              />
            </b-col>

            <b-col cols="6">
              <b-row>
                <b-col>
                  <b-custom-input
                    id="registration-number"
                    required
                    :label="$t('Registration Number')"
                    :value="company.registrationNumber"
                    class="shadow rounded"
                    @blur="updateField('registrationNumber', $event)"
                  />
                </b-col>
                <b-col>
                  <b-custom-input
                    id="vat-number"
                    required
                    :label="$t('VAT Number')"
                    :value="company.vatNumber"
                    class="shadow rounded"
                    @blur="updateField('vatNumber', $event)"
                  />
                </b-col>
              </b-row>

              <b-custom-input
                id="zip-code"
                required
                :label="$t('Zipcode')"
                :value="company.address && company.address.zipCode"
                class="shadow rounded"
                @blur="updateAddressProperty('zipCode', $event)"
              />

              <b-custom-input
                id="country"
                required
                :label="$t('Country')"
                :value="company.address && company.address.country"
                class="shadow rounded"
                @blur="updateAddressProperty('country', $event)"
              />

              <phone-number-field
                required
                :value="company.phone"
                :label="$t('Phone')"
                class="shadow rounded"
                @input="updateField('phone', $event)"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-custom-textarea
                ref="comments"
                :value="company.comment"
                :placeholder="$t('Comment')"
                :rows="1"
                class="shadow rounded"
                @blur="updateField('comment', $event)"
              />
            </b-col>
          </b-row>
        </b-col>

        <b-col cols="4">
          <b-card class="activity-container shadow">
            <h3>{{ $t('Activity') }}</h3>

            <b-custom-dropdown
              id="role"
              required
              :value="company.role"
              :label="$t('Status hängavtal')"
              :options="roleList"
              :placeholder="$t('Status hängavtal')"
              @input="updateField('role', $event)"
            />

            <form-label-group :label="$t('Occupation')" class="mb-2 company-info">
              <div class="tooltip-icon">
                <b-icon v-tooltip="infoTooltip" icon="info-circle-fill" class="cursor-help" />
              </div>

              <multiselect
                :value="company.mainBusiness"
                id="main-business"
                taggable
                multiple
                :options="occupations"
                :placeholder="$t('Occupation')"
                :tag-placeholder="$t('Add New')"
                class="boon-multiselect-v2 boon-multiselect-v2__internal main-business"
                @input="updateField('mainBusiness', $event)"
                @tag="addMainBusiness"
              >
                <div slot="noResult">
                  {{ $t('No match found, add new...') }}
                </div>
              </multiselect>
            </form-label-group>

            <b-custom-switch
              :value="company.usedBefore"
              :disabled="!company._id"
              :label="$t('Used Before')"
              @input="updateField('usedBefore', $event)"
            />

            <b-custom-switch
              :value="company.doNotUse"
              :disabled="!company._id"
              :label="$t('Do not use')"
              @input="updateField('doNotUse', $event)"
            />
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <b-container>
      <b-row class="my-2">
        <b-col class="d-flex align-items-center justify-center">
          <h3 class="m-0">{{ $t('Documents & Certificates') }}</h3>
        </b-col>
        <b-col cols="5">
          <b-row class="register-link-controls">
            <b-col>
              <b-custom-dropdown
                :value="linkProject && linkProject._id"
                id="select-project"
                :label="$t('Select project')"
                :options="projectIdList"
                :placeholder="$t('Select project')"
                text-field="title"
                value-field="_id"
                @input="onProjectChange"
              />
            </b-col>

            <b-col cols="auto" class="pb-2">
              <b-button
                pill
                variant="success"
                :title="$t('Generate new registration link that can be used by subcontractor to enter their details')"
                :disabled="!linkProject._id"
                @click="onCreateRegistrationLinkClick"
              >
                {{ $t('Generate link') }}
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <company-files :company="company" />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <company-files-legacy :company="company" />
        </b-col>
      </b-row>
    </b-container>
  </b-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import moment from 'moment';

import constants from '@/constants';

import BCustomInput from '@/components/organisation/BCustomInput';
import BCustomSwitch from '@/components/organisation/BCustomSwitch';
import BCustomDropdown from '@/components/organisation/BCustomDropdown';
import BCustomTextarea from '@/components/organisation/BCustomTextarea';
import PhoneNumberField from '@/components/form/PhoneNumberFieldLegacy';
import FormLabelGroup from '@/components/organisation/FormLabelGroup';

import CompanyFiles from './CompanyFiles';
import CompanyFilesLegacy from './CompanyFilesLegacy';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

export default {
  name: 'EditCompany',

  components: {
    BCustomInput,
    BCustomSwitch,
    BCustomDropdown,
    BCustomTextarea,
    FormLabelGroup,
    PhoneNumberField,
    Multiselect,
    ConfirmDeleteDialog,
    CompanyFiles,
    CompanyFilesLegacy,
  },

  data() {
    return {
      company: null,
      linkProject: {},
    };
  },

  computed: {
    ...mapGetters(['getCompany', 'getProject', 'hasSimplePermissions', 'occupations', 'projectIdList']),

    roleList() {
      return constants.companyRoles.map((value) => ({ value, text: this.$t(value) }));
    },

    registrationLinks() {
      if (!this.company || !this.company.registrationLinks) {
        return [];
      }

      const today = moment().format(constants.formats.dateShort);

      return this.company.registrationLinks.map(({ _id, linkMailTo, projectId, expiryDate, link }) => {
        const project = this.getProject(projectId);
        const description = project ? [project.postcode, project.title].filter(Boolean).join(' ') : '';
        return {
          _id,
          linkMailTo,
          expiryDate,
          link,
          expired: today > expiryDate,
          title: project ? project.title : '',
          description,
          tooltip: {
            content: description,
            placement: 'top',
            offset: 20,
            delay: { show: 200, hide: 200 },
            classes: ['tooltip-bubble'],
          },
        };
      });
    },

    infoTooltip() {
      return {
        content: this.$t('company-info'),
        placement: 'right',
        offset: 20,
        delay: { show: 200, hide: 200 },
        classes: ['tooltip-bubble'],
      };
    },
  },

  methods: {
    ...mapActions([
      'getDefaultCompany',
      'saveCompanyProperties',
      'addCompanyRegistrationLink',
      'mailCompanyRegistrationLink',
    ]),

    onProjectChange(value) {
      this.linkProject = this.projectIdList.find(({ _id }) => value === _id);
    },

    async updateField(fieldName, newValue) {
      if (this.company[fieldName] !== newValue) {
        const oldId = this.company._id;
        const params = {
          updateCompany: this.company,
          newCompanyProperties: {
            [fieldName]: newValue,
          },
        };

        let savedCompany;
        try {
          savedCompany = await this.saveCompanyProperties(params);
        } catch (error) {
          console.error(`Failed to update company property ${fieldName}`, error);
        }

        if (savedCompany && oldId !== savedCompany._id) {
          this.company = savedCompany;
          this.$emit('input', savedCompany._id);
        }
      }
    },

    updateAddressProperty(property, value) {
      if (this.company.address && this.company.address[property] === value) {
        return;
      }

      this.saveCompanyProperties({
        updateCompany: this.company,
        newCompanyProperties: {
          address: Object.assign({}, this.company.address, { [property]: value }),
        },
      });
    },

    addMainBusiness(value) {
      this.updateField('mainBusiness', [...this.company.mainBusiness, value]);
    },

    onCreateRegistrationLinkClick() {
      this.addCompanyRegistrationLink({ company: this.company, project: this.linkProject });
    },
  },

  watch: {
    $route: {
      immediate: true,
      async handler(next, prev) {
        if (!next || !next.params._id) {
          this.company = await this.getDefaultCompany();
        } else if (!prev || prev.params._id !== next.params._id) {
          this.company = this.getCompany(this.$route.params._id);
        }
      },
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: 'DIN Alternate Bold';
  /* src: url('location.eot'); */
  src:
        /* url('location.eot#iefix') format('embedded-opentype'), */ url('/static/fonts/DINAlternate-Bold.woff')
      format('woff'),
    url('/static/fonts/DINAlternate-Bold.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: 'DIN-Light';
  src: url('/static/fonts/DINLight.woff') format('woff'); /* Modern Browsers */
  font-weight: normal;
  font-style: normal;
}

.form-title {
  line-height: 1.1em;
}

.close-button {
  font-size: 1rem;
  padding: 0.375rem 2rem;
}

/* used */
.boon-textarea {
  width: 100%;
  border: 1px solid #ccc;
  background: #fff;
  padding: 2px;
}

.register-link-controls {
  display: flex;
  align-items: center;
}

.activity-container {
  /* Same margin as for the last field in sibling <b-col> */
  height: calc(100% - 8px);
}

.tooltip-icon >>> .b-icon {
  width: 0.875rem;
}

.form-container {
  margin-top: 16px;
}

.main-business >>> .multiselect__tags-wrap {
  display: inline;
  position: absolute;
  top: 55px;
}

.company-info >>> .main-business ~ label {
  padding-top: calc(var(--input-padding-y) / 3);
  padding-bottom: calc(var(--input-padding-y) / 3);
  font-size: 12px;
  color: #777;
}

.delete-button {
  font-size: 1.25rem;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/EditCompany/index.vue